import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './FeedItem.styled';
import * as StyledPreview from './FeedItemPreview.styled';
import { PlaceholderAvatar, Avatar as StyledAvatar } from '../ActivityFeed.styled';
import { timeAgo } from '../timeAgo';
import { VisuallyHiddenText } from '../../../styles/mixins/visually-hidden';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Feed item preview.
 *
 * Currently used on the add reply modal (see AddCommentModal.tsx).
 *
 * Separate component for two reasons:
 * - ESLint was throwing a circular dependency error when trying to reuse the FeedItem component
 *   in the AddCommentModal component. Originally, added this FeedItem.tsx, but still got that
 *   same circular dependency error. Moving it to its own file is the only way to resolve it.
 */
export const FeedItemPreview = ({
  avatar,
  name,
  created,
  content
}) => {
  const {
    t
  } = useTranslation('blocks.activityFeed');
  const [expandPreview, toggleExpandPreview] = React.useState(false);
  const [cssClampActive, setCssClampActive] = React.useState(false);
  const previewContentRef = React.useRef(null);
  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };
  React.useEffect(() => {
    if (previewContentRef?.current) {
      /**
       * https://stackoverflow.com/a/67455839
       *
       * Detecting CSS line-clamp by comparing the scrollHeight
       * and the clientHeight of the "clamped" element.
       */
      setCssClampActive(previewContentRef.current.scrollHeight > previewContentRef.current.clientHeight);
    }
  }, []);
  const createdDate = new Date(created);
  const timeAgoObj = timeAgo(createdDate);
  return /*#__PURE__*/_jsxs(StyledPreview.FeedItemContainer, {
    onClick: handleClick,
    children: [/*#__PURE__*/_jsx(StyledPreview.AvatarContainer, {
      children: avatar ? /*#__PURE__*/_jsx(StyledAvatar, {
        src: avatar,
        width: "36",
        height: "36",
        alt: ""
      }) : /*#__PURE__*/_jsx(PlaceholderAvatar, {})
    }), /*#__PURE__*/_jsxs(Styled.ContentContainer, {
      children: [/*#__PURE__*/_jsxs(Styled.Header, {
        children: [/*#__PURE__*/_jsx(Styled.Name, {
          children: name
        }), /*#__PURE__*/_jsxs(Styled.Time, {
          dateTime: created,
          children: [/*#__PURE__*/_jsx("span", {
            "aria-hidden": true,
            children: timeAgoObj.short
          }), /*#__PURE__*/_jsx(VisuallyHiddenText, {
            children: timeAgoObj.full
          })]
        })]
      }), /*#__PURE__*/_jsx(Styled.Content, {
        ref: previewContentRef,
        dangerouslySetInnerHTML: {
          __html: content
        },
        className: expandPreview ? undefined : 'truncate-feed-item-preview'
      }), (cssClampActive || expandPreview) && /*#__PURE__*/_jsx(Styled.ShowMore, {
        "aria-hidden": true,
        onClick: () => toggleExpandPreview(!expandPreview),
        children: expandPreview ? t('FeedItemPreview.showLess', 'Show less') : t('FeedItemPreview.showMore', 'Show more')
      })]
    })]
  });
};